import { CommonModule, DatePipe, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, resource, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';

import { ActivityStream } from '@lib/activity-stream';
import { AddonTable } from '@lib/addon-table';
import { AgreementDetailTable } from '@lib/agreement-detail-table';
import { AgreementForm, AgreementFormData } from '@lib/agreement-form';
import { ExtraHoursTable } from '@lib/extra-hours-table';
import { ReservationData, ReservationDialog } from '@lib/reservation-dialog';

import { AgreementService, Auth, Jira, Lambda } from '../service';
import { ExportDocumentationData, ExportDocumentationForm } from './export-documentation-form';
import { ProjectTable } from './project-table';
import { TimeReportingWidget } from './time-reporting-widget';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './agreement-detail-page.html',
  imports: [
    DatePipe,
    MatListModule,
    TimeReportingWidget,
    ActivityStream,
    CommonModule,
    MatIconModule,
    MatToolbarModule,
    MatButtonModule,
    MatTooltipModule,
    ExtraHoursTable,
    AgreementDetailTable,
    MatChipsModule,
    MatProgressSpinnerModule,
    AddonTable,
    ProjectTable,
  ],
})
export class AgreementDetailPage implements OnInit {
  dialog = inject(MatDialog);

  protected eventType = 'agreement-details';

  agreementNumber = input.required<string>();

  agreement = computed(() => this.itemService.agreements().find((it) => it.AgreementNumber === this.agreementNumber()));

  authService = inject(Auth);
  itemService = inject(AgreementService);
  route = inject(ActivatedRoute);
  location = inject(Location);
  jira = inject(Jira);

  kbLink = inject(Lambda).getKnowledgeBaseLink();

  pending = signal(false);

  probablyHoldedResource = resource({
    params: () => ({ agreement: this.agreement() }),
    defaultValue: false,
    loader: async ({ params }) => {
      if (!params.agreement) {
        return false;
      }
      const logs = await this.jira.getProjectTimelogs(params.agreement);
      const spentRecently = Math.max(...logs.time.slice(-3).map((it) => it[1].spent));
      return spentRecently <= 0;
    },
  });

  probablyHolded = this.probablyHoldedResource.value;

  ngOnInit(): void {
    if (this.route.snapshot.params['edit-form']) {
      this.edit();
    }
  }

  viewDocument(id: string): void {
    this.itemService.downloadDocument(id);
  }

  reloadEntity(): void {
    this.itemService.reload();
  }

  isExpired = computed(() => {
    const agreement = this.agreement();

    if (!agreement) {
      return false;
    }
    const result = Math.ceil((new Date(agreement.EndDate).getTime() - new Date().getTime()) / 1000 / 3600 / 24);
    return result < 0;
  });

  edit() {
    const agreement = this.agreement();
    if (!agreement) {
      console.warn('Attempt to open edit form without an agreement');
      return;
    }

    const data: AgreementFormData = {
      agreement,
      title: 'Edit agreement',
    };
    this.dialog
      .open(AgreementForm, {
        data,
      })
      .afterClosed()
      .subscribe({
        next: (result) => {
          if (result) {
            this.reloadEntity();

            const data: ReservationData = { id: agreement.AgreementNumber };
            this.dialog.open(ReservationDialog, { data });
          }
        },
      });
  }

  exportDocumentation() {
    const entity = this.agreement();
    if (!entity) {
      console.warn('Attempt to export documentation without an agreement');
      return;
    }
    const data: ExportDocumentationData = { entity };
    this.dialog.open(ExportDocumentationForm, {
      data,
    });
  }
}
